import { useState } from "react";
import { useWellnessState } from "@/context/WellnessStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { wellnessReviewPrompts, periodLabel } from "@/lib/wellness";
import { stripHtml, periodKey } from "@/lib/life";
import { PageHeader, SectionCard, ProgressBar } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function WellnessReviews() {
  const { state, update, loading } = useWellnessState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [expanded, setExpanded] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const prompts = wellnessReviewPrompts[horizon] || wellnessReviewPrompts.Week;
  const key = horizon + ":" + periodKey(horizon, date);
  const answers = state.reviews?.[key] || {};
  const answered = prompts.filter((_, i) => stripHtml(answers[i])).length;
  const setAnswer = (i, val) => update((prev) => { const r = { ...(prev.reviews || {}) }; r[key] = { ...(r[key] || {}), [i]: val }; return { ...prev, reviews: r }; }, `Updated ${horizon} wellness review`);

  return (
    <div>
      <PageHeader title="Reviews" subtitle="Reflect on your body, energy and presence — celebrate progress, adjust with grace." testid="wellness-reviews-header" />

      <div className="mb-4"><PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="wreview-period" /></div>

      <SectionCard className="mb-4" testid="wellness-review-progress">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-serif-display text-xl text-[var(--text)]">{horizon} review · <span className="text-[var(--text-muted)]">{periodLabel(horizon, date)}</span></h3>
          <div className="min-w-[140px]"><div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1"><span>Reflected</span><span className="tabular-nums">{answered}/{prompts.length}</span></div><ProgressBar value={answered} max={prompts.length} tone="green" /></div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {prompts.map((p, i) => (
          <SectionCard key={i} title={p} testid={`wreview-prompt-${i}`}>
            <RichTextEditor value={answers[i] || ""} onChange={(html) => setAnswer(i, html)} placeholder="Reflect honestly… bold what matters, list your takeaways" onExpand={() => setExpanded({ i, p })} testid={`wreview-input-${i}`} />
          </SectionCard>
        ))}
      </div>

      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent className="max-w-3xl" data-testid="wreview-expand-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl">{expanded?.p}</DialogTitle></DialogHeader>
          {expanded && (<div><RichTextEditor value={answers[expanded.i] || ""} onChange={(html) => setAnswer(expanded.i, html)} placeholder="Reflect freely here…" minHeight={340} testid="wreview-expand-editor" /><div className="flex justify-end mt-4"><Button onClick={() => setExpanded(null)} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="wreview-expand-done">Done</Button></div></div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
